import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { STEP_LABELS, TOTAL_STEPS } from "../types";
import { ApiError } from "../types";
import { validateStep } from "../validation";
import type { useStepValidation } from "../hooks";
import { saveCurrentDraft } from "../services/saveDraft";
import { markSubmitInFlight, clearSubmitInFlight } from "../services/submitInFlight";
import { getApi } from "../api";
import { Spinner } from "./Spinner";
import { MULTI_EMPLOYER_UNSUPPORTED_MSG, isUnsupportedMultiEmployer, PEO_UNSUPPORTED_MSG, isUnsupportedPeoMasterPlan } from "../types/structureConstants";

interface BottomNavProps {
  validation: ReturnType<typeof useStepValidation>;
  /** Read-only review mode (submitted form, or external user on a correction) — shows the locked footer. */
  reviewMode?: boolean;
}

export function BottomNav({ validation, reviewMode = false }: BottomNavProps) {
  const currentStep = useFormStore((s) => s.currentStep);
  const data = useFormStore((s) => s.data);
  const formVersion = useFormStore((s) => s.formVersion);
  const saveStatus = useFormStore((s) => s.saveStatus);
  const nextStep = useFormStore((s) => s.nextStep);
  const prevStep = useFormStore((s) => s.prevStep);
  const isSubmitting = useUiStore((s) => s.isSubmitting);
  const welcomeScrollComplete = useUiStore((s) => s.welcomeScrollComplete);
  const addToast = useUiStore((s) => s.addToast);

  const isSaving = saveStatus === "saving";
  const navDisabled = isSubmitting || isSaving;

  const dismissValidation = () => {
    useUiStore.getState().dismissValidationModal();
  };

  const isWelcomeStep = currentStep === 0;
  const isFirstStep = currentStep <= 0;
  const isLastStep = currentStep === TOTAL_STEPS;

  const proceedToNext = async () => {
    dismissValidation();
    const ok = await saveCurrentDraft();
    if (!ok) return;
    nextStep();
    validation.clearErrors();
  };

  const handleContinue = async () => {
    if (isWelcomeStep) {
      await proceedToNext();
      return;
    }

    const result = validateStep(currentStep, data);
    // Dev-only override: when the validation toast is disabled, treat invalid
    // fields as auto-Ignore so dev can skip ahead without the gate.
    if (!result.isValid && useUiStore.getState().showValidationToast) {
      validation.touchAll(currentStep);
      useUiStore.getState().showValidationModal(() => { void proceedToNext(); });
      useUiStore.setState((s) => ({ validationTouchCounter: s.validationTouchCounter + 1 }));
      return;
    }

    await proceedToNext();
  };

  const handleBack = async () => {
    dismissValidation();
    const ok = await saveCurrentDraft();
    if (!ok) return;
    prevStep();
    validation.clearErrors();
  };

  const handleSubmit = async () => {
    // Hard stops — unsupported plan structures. Surface every applicable reason
    // (a form can trip more than one), then block before any other validation.
    const hardStopMessages: string[] = [];
    if (isUnsupportedPeoMasterPlan(data)) hardStopMessages.push(PEO_UNSUPPORTED_MSG);
    if (isUnsupportedMultiEmployer(data)) hardStopMessages.push(MULTI_EMPLOYER_UNSUPPORTED_MSG);
    if (hardStopMessages.length > 0) {
      hardStopMessages.forEach((msg) => addToast(msg, "error"));
      return;
    }

    // Validate ALL steps before submit
    const invalidSteps: string[] = [];
    for (let s = 1; s <= TOTAL_STEPS; s++) {
      if (!validateStep(s, data).isValid) {
        invalidSteps.push(STEP_LABELS[s - 1]);
      }
    }

    if (invalidSteps.length > 0) {
      validation.touchAll(currentStep);
      useUiStore.setState((s) => ({ validationTouchCounter: s.validationTouchCounter + 1 }));
      // Idempotent: if the validation toast is still on screen (not yet exiting),
      // don't stack another. Spam-clicking Submit should not produce a tower.
      const { validationToastId, toasts } = useUiStore.getState();
      const stillShowing = validationToastId != null
        && toasts.some((t) => t.id === validationToastId && !t.exiting);
      if (!stillShowing) {
        const id = addToast(
          "Cannot submit — see text in red and click the edit icon to correct.",
          "error",
        );
        useUiStore.setState({ validationToastId: id });
      }
      return;
    }

    const { formId } = useFormStore.getState();
    useUiStore.getState().setSubmitting(true);
    // Mark before the network call so a refresh between this line and the response
    // still leaves a signal that the post-refresh FormPage load can detect and
    // poll on. Cleared on every terminal outcome (success, confirmed failure,
    // recovered-after-timeout) below.
    markSubmitInFlight(formId!);
    try {
      const api = getApi();
      const result = await api.submitIntakeForm(formId!);
      useFormStore.getState().setFormStatus(result.status);
      clearSubmitInFlight(formId!);
    } catch (err) {
      // The submit endpoint runs a cross-object SF write that the backend treats as
      // best-effort to complete even if the client aborts. So a timeout/network/5xx
      // error here does NOT prove SF rejected the submission — it can complete on the
      // server while the client gives up. Without a recovery check the user would sit
      // on a "draft" that SF considers Submitted and silently lose later edits
      // (UpdateDraftAsync skips writes when Status=Submitted). Refetch the form on
      // ambiguous failures and reflect the truth.
      const ambiguous =
        !(err instanceof ApiError) || err.status === 408 || err.status >= 500;
      if (ambiguous) {
        try {
          const fresh = await getApi().getIntakeForm(formId!);
          if (fresh.status === "submitted") {
            useFormStore.getState().setFormStatus("submitted");
            clearSubmitInFlight(formId!);
            addToast("Submission successful.", "success");
            return;
          }
        } catch {
          // Refetch failed too — fall through to the generic error toast below.
        }
      }
      // Confirmed failure (or ambiguous + refetch confirms still in-progress): submit
      // genuinely didn't take. Clear the signal so a subsequent reload doesn't poll
      // a write that already gave up.
      clearSubmitInFlight(formId!);
      addToast("Submission failed. Please try again.", "error");
    } finally {
      useUiStore.getState().setSubmitting(false);
    }
  };

  if (reviewMode) {
    return (
      <div role="status" className="sticky bottom-0 bg-brand-green-light border-t border-brand-green px-4 py-3 text-center">
        <span className="text-brand-green-dark font-medium">
          This form is read-only.
        </span>
      </div>
    );
  }

  return (
    <nav
      className="sticky bottom-0 z-40 bg-white border-t border-gray-200 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      aria-label="Form navigation"
    >
      <div className="max-w-[600px] mx-auto px-6 flex items-center gap-3">
        {!isFirstStep && (
          <button
            onClick={handleBack}
            disabled={navDisabled}
            className="flex-1 rounded-lg bg-gray-200 py-3.5 text-base font-medium text-gray-800 hover:bg-gray-300 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-green-50"
          >
            Back
          </button>
        )}

        {isLastStep ? (
          <button
            onClick={handleSubmit}
            disabled={navDisabled}
            className="flex-[1.618] rounded-lg bg-brand-green py-3.5 text-base font-medium text-white hover:bg-brand-green-hover disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-green-50"
          >
            {isSubmitting && <Spinner size="sm" label={null} />}
            {/* v1 (first submission) reads "Submit Intake Form"; a corrected version (v2+) reads "Update Intake Form" (ADO-3417). */}
            <span>{(formVersion ?? 1) >= 2 ? "Update" : "Submit"}<span className="hidden sm:inline"> Intake Form</span></span>
          </button>
        ) : (
          <button
            onClick={handleContinue}
            disabled={navDisabled || (isWelcomeStep && !welcomeScrollComplete)}
            className={`rounded-lg bg-brand-green py-3.5 px-8 text-base font-medium text-white hover:bg-brand-green-hover disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${isWelcomeStep ? "flex-1 md:flex-none md:mx-auto md:px-20" : "flex-[1.618]"}`}
          >
            {isSaving && <Spinner size="sm" label={null} />}
            {isWelcomeStep ? "Begin" : "Continue"}
          </button>
        )}
      </div>
    </nav>
  );
}
